/**
 * Advanced Leads Management Page
 * Features: Drag & Drop Kanban, Search, Filters, Pagination, Temperature Categorization
 * Based on New Folder UI/UX with Glassmorphism Design
 */

import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Filter,
  Download,
  Phone,
  Mail,
  Calendar,
  MapPin,
  Edit,
  Trash2,
  GripVertical,
  X,
  Clock,
  DollarSign,
  Flame,
  Thermometer,
  Snowflake,
  Sun,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Upload
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// ==================== CONSTANTS ====================

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const LEAD_STATUSES = {
  new: { label: 'New', color: 'bg-red-500', icon: '🆕' },
  contacted: { label: 'Contacted', color: 'bg-orange-500', icon: '📞' },
  qualified: { label: 'Qualified', color: 'bg-yellow-500', icon: '⭐' },
  viewing_scheduled: { label: 'Viewing Scheduled', color: 'bg-green-500', icon: '📅' },
  negotiating: { label: 'Negotiating', color: 'bg-blue-500', icon: '💰' },
  closed_won: { label: 'Closed Won', color: 'bg-green-600', icon: '🎉' },
  closed_lost: { label: 'Closed Lost', color: 'bg-gray-500', icon: '❌' },
};

const TEMPERATURE_CONFIG = {
  burning: { emoji: '🔥', color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'BURNING', icon: Flame },
  hot: { emoji: '🌶️', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'HOT', icon: Thermometer },
  warm: { emoji: '☀️', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'WARM', icon: Sun },
  cold: { emoji: '❄️', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'COLD', icon: Snowflake },
};

const PURPOSE_LABELS = {
  investment: '📈 Investment',
  living: '🏡 Living',
  residency: '🛂 Residency/Visa',
};

// ==================== API HELPERS ====================

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const api = {
  async get(endpoint) {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`API Error: ${response.statusText}`);
    return response.json();
  },
  async patch(endpoint, data) {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`API Error: ${response.statusText}`);
    return response.json();
  },
  async post(endpoint, data) {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`API Error: ${response.statusText}`);
    return response.json();
  },
  async delete(endpoint) {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`API Error: ${response.statusText}`);
    return response.json();
  },
};

// ==================== LEAD CARD COMPONENT ====================

const LeadCard = ({ lead, index, isDragging, onScheduleClick }) => {
  const tempConfig = TEMPERATURE_CONFIG[lead?.temperature] || TEMPERATURE_CONFIG.cold;

  return (
    <div className={`glass-card rounded-xl p-4 cursor-move hover:shadow-gold transition-all ${
      isDragging ? 'opacity-50 rotate-2' : ''
    }`}>
      {/* Drag Handle */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <GripVertical size={16} className="text-white/30" />
          <h4 className="text-white font-semibold text-sm">{lead?.name || lead?.full_name || 'Anonymous'}</h4>
        </div>
        {/* Temperature Badge */}
        <span className={`${tempConfig.color} px-2 py-1 rounded-md text-xs font-bold border flex items-center gap-1`}>
          {tempConfig.emoji} {tempConfig.label}
        </span>
      </div>

      {/* Lead Info */}
      <div className="space-y-2 text-xs text-white/70">
        {lead.phone && (
          <div className="flex items-center gap-2">
            <Phone size={14} className="text-gold-500" />
            <span>{lead.phone}</span>
          </div>
        )}
        {lead.email && (
          <div className="flex items-center gap-2">
            <Mail size={14} className="text-gold-500" />
            <span className="truncate">{lead.email}</span>
          </div>
        )}
        {(lead?.budget_min || lead?.budget_max) && (
          <div className="flex items-center gap-2">
            <DollarSign size={14} className="text-gold-500" />
            <span className="text-gold-400 font-semibold">
              {lead.budget_min ? `${(Number(lead.budget_min)/1000000).toFixed(1)}M` : ''} 
              {lead.budget_min && lead.budget_max ? ' - ' : ''}
              {lead.budget_max ? `${(Number(lead.budget_max)/1000000).toFixed(1)}M` : ''}
            </span>
          </div>
        )}
        {lead.purpose && (
          <div className="flex items-center gap-2">
            <span>{PURPOSE_LABELS[lead.purpose] || lead.purpose}</span>
          </div>
        )}
        {/* Scheduled Followup Badge */}
        {lead.next_followup_at && (
          <div className="flex items-center gap-2 mt-2 p-1.5 bg-blue-500/20 text-blue-300 rounded-lg border border-blue-500/30">
            <Clock size={12} className="text-blue-400" />
            <span className="font-semibold">
              {lead.conversation_data?.scheduled_followup_type === 'fomo' ? 'Urgent Follow-up' : 'Offer Follow-up'} scheduled for {new Date(lead.next_followup_at).toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* Lead Score & Interactions */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
        <div className="flex items-center gap-2">
          <TrendingUp size={14} className="text-green-400" />
          <span className="text-white/60 text-xs">Score: <strong className="text-green-400">{lead.lead_score || 0}</strong></span>
        </div>
        <div className="flex items-center gap-2 text-white/40 text-xs">
          {(lead?.total_interactions || 0) > 0 && (
            <span>💬 {lead.total_interactions}</span>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 mt-3 flex-wrap">
        <button 
          onClick={() => onScheduleClick(lead)}
          className="flex-1 px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 text-xs font-semibold hover:bg-blue-500/20 transition-colors whitespace-nowrap">
          <Clock size={12} className="inline mr-1" />
          Schedule
        </button>
        <button className="px-3 py-1.5 rounded-lg bg-gold-500/10 text-gold-500 text-xs font-semibold hover:bg-gold-500/20 transition-colors">
          <Edit size={12} />
        </button>
        <button className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-xs font-semibold hover:bg-red-500/20 transition-colors">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
};

// ==================== KANBAN COLUMN COMPONENT ====================

const KanbanColumn = ({ status, statusConfig, leads, provided, snapshot, onScheduleClick }) => (
  <div className="flex-1 min-w-[300px]">
    <div className="glass-card rounded-2xl p-5">
      {/* Column Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{statusConfig.icon}</span>
          <h3 className="text-white font-semibold">{statusConfig.label}</h3>
        </div>
        <span className={`${statusConfig.color} text-white text-xs px-3 py-1.5 rounded-full font-bold`}>
          {leads.length}
        </span>
      </div>

      {/* Droppable Area */}
      <div
        ref={provided.innerRef}
        {...provided.droppableProps}
        className={`space-y-3 min-h-[400px] max-h-[600px] overflow-y-auto pr-2 ${
          snapshot.isDraggingOver ? 'bg-white/5 rounded-xl' : ''
        }`}
      >
        {leads.map((lead, index) => (
          <Draggable key={lead.id} draggableId={String(lead.id)} index={index}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
              >
                <LeadCard lead={lead} index={index} isDragging={snapshot.isDragging} onScheduleClick={onScheduleClick} />
              </div>
            )}
          </Draggable>
        ))}
        {provided.placeholder}
        
        {leads.length === 0 && (
          <div className="text-center py-12 text-white/30">
            <p>No leads in this stage</p>
            <p className="text-xs mt-2">Drag leads here</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ==================== MAIN COMPONENT ====================

const AdvancedLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTemperature, setFilterTemperature] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'table'
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState({ name: '', phone: '', email: '', purpose: 'investment' });
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Schedule Modal State
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedScheduleLead, setSelectedScheduleLead] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ message_type: 'fomo', scheduled_at: '', custom_prompt: '' });
  const [scheduleLoading, setScheduleLoading] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, searchQuery, filterStatus, filterTemperature]);

  const fetchLeads = async () => {
    try {
      const tenantId = localStorage.getItem('tenantId') || '1';
      const response = await api.get(`/api/tenants/${tenantId}/leads?limit=1000`);
      const leadsData = Array.isArray(response) ? response : (response.leads || []);
      setLeads(leadsData);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      // Set empty array on error to prevent crashes
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/["']/g, ''));
    const nameIdx = headers.findIndex(h => ['name', 'نام', 'full name', 'fullname'].includes(h));
    const phoneIdx = headers.findIndex(h => ['phone', 'mobile', 'tel', 'تلفن', 'شماره', 'phone number'].includes(h));
    const emailIdx = headers.findIndex(h => ['email', 'ایمیل', 'e-mail', 'mail'].includes(h));
    if (nameIdx === -1) return [];
    return lines.slice(1).map(line => {
      const cols = line.match(/("[^"]*"|[^,]*)/g)?.map(c => c.replace(/^"|"$/g, '').trim()) || line.split(',').map(c => c.trim());
      return {
        name: cols[nameIdx] || '',
        phone: phoneIdx !== -1 ? (cols[phoneIdx] || '') : '',
        email: emailIdx !== -1 ? (cols[emailIdx] || '') : '',
        source: 'manual',
      };
    }).filter(r => r.name);
  };

  const handleFileUpload = (file) => {
    if (!file) return;
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      setImportData(parsed);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileUpload(e.dataTransfer.files[0]);
  };

  const handleImportSubmit = async () => {
    if (!importData.length) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const tenantId = localStorage.getItem('tenantId') || '1';
      const result = await api.post(`/api/tenants/${tenantId}/leads/import`, { leads: importData });
      setImportResult(result);
      if (result.created > 0) {
        fetchLeads();
      }
    } catch (err) {
      setImportResult({ message: 'Import failed: ' + err.message, created: 0, skipped: importData.length, errors: [err.message] });
    } finally {
      setImportLoading(false);
    }
  };

  const handleScheduleClick = (lead) => {
    setSelectedScheduleLead(lead);
    setScheduleForm({
      message_type: lead.conversation_data?.scheduled_followup_type || 'fomo',
      scheduled_at: lead.next_followup_at ? new Date(lead.next_followup_at).toISOString().slice(0, 16) : '',
      custom_prompt: lead.conversation_data?.scheduled_custom_prompt || ''
    });
    setIsScheduleModalOpen(true);
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    if (!scheduleForm.scheduled_at) return;
    
    setScheduleLoading(true);
    try {
      const tenantId = localStorage.getItem('tenantId') || '1';
      await api.post(`/api/tenants/${tenantId}/leads/${selectedScheduleLead.id}/schedule-followup`, {
        message_type: scheduleForm.message_type,
        scheduled_at: new Date(scheduleForm.scheduled_at).toISOString(),
        custom_prompt: scheduleForm.custom_prompt
      });
      setIsScheduleModalOpen(false);
      fetchLeads(); // Refresh leads to show the badge
    } catch (err) {
      alert('Failed to schedule follow-up: ' + err.message);
    } finally {
      setScheduleLoading(false);
    }
  };

  const filterLeads = () => {
    let filtered = [...leads];

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(lead => lead.status === filterStatus);
    }

    // Temperature filter
    if (filterTemperature !== 'all') {
      filtered = filtered.filter(lead => lead.temperature === filterTemperature);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(lead =>
        lead?.name?.toLowerCase().includes(query) ||
        lead?.full_name?.toLowerCase().includes(query) ||
        lead?.phone?.toLowerCase().includes(query) ||
        lead?.email?.toLowerCase().includes(query) ||
        lead?.company_name?.toLowerCase().includes(query)
      );
    }

    setFilteredLeads(filtered);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;
    
    // If dropped in same column, no change needed
    if (source.droppableId === destination.droppableId) return;

    const leadId = parseInt(draggableId);
    const newStatus = destination.droppableId;

    // Optimistically update UI
    const updatedLeads = leads.map(lead =>
      lead.id === leadId ? { ...lead, status: newStatus } : lead
    );
    setLeads(updatedLeads);

    // Update backend
    try {
      const tenantId = localStorage.getItem('tenantId') || '1';
      await api.patch(`/api/tenants/${tenantId}/leads/${leadId}`, { status: newStatus });
      // Lead status updated successfully
    } catch (error) {
      console.error('Failed to update lead status:', error);
      // Revert on error
      fetchLeads();
    }
  };

  const exportToExcel = () => {
    // CSV export functionality
    const csvContent = [
      ['Name', 'Phone', 'Email', 'Budget Min', 'Budget Max', 'Purpose', 'Status', 'Temperature', 'Lead Score'],
      ...filteredLeads.map(lead => [
        lead.name || '',
        lead.phone || '',
        lead.email || '',
        lead.budget_min || '',
        lead.budget_max || '',
        lead.purpose || '',
        lead.status || '',
        lead.temperature || '',
        lead.lead_score || 0
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Group leads by status for Kanban view
  const leadsByStatus = Object.keys(LEAD_STATUSES).reduce((acc, status) => {
    acc[status] = filteredLeads.filter(lead => lead.status === status);
    return acc;
  }, {});

  // Pagination
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLeads = filteredLeads.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Lead Management</h1>
          <p className="text-white/60">
            Total: <strong className="text-gold-500">{filteredLeads.length}</strong> leads
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportToExcel}
            className="btn-gold flex items-center gap-2"
          >
            <Download size={18} />
            Export to Excel
          </button>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="btn-gold flex items-center gap-2"
          >
            <Plus size={18} />
            Add Lead
          </button>
          <button 
            onClick={() => { setIsImportModalOpen(true); setImportData([]); setImportResult(null); }}
            className="btn-gold flex items-center gap-2"
          >
            <Upload size={18} />
            Import CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(TEMPERATURE_CONFIG).map(([key, config]) => {
          const count = filteredLeads.filter(l => l.temperature === key).length;
          const Icon = config.icon;
          return (
            <div
              key={key}
              className="glass-card rounded-xl p-5 cursor-pointer hover:shadow-gold transition-all"
              onClick={() => setFilterTemperature(filterTemperature === key ? 'all' : key)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm mb-1">{config.label} Leads</p>
                  <p className="text-3xl font-bold text-white">{count}</p>
                </div>
                <div className={`${config.color} p-4 rounded-xl border`}>
                  <Icon size={28} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters Bar */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
              <input
                type="text"
                placeholder="Search leads by name, phone, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-navy-800/50 text-white pl-12 pr-4 py-3 rounded-xl border border-white/10 focus:border-gold-500 focus:outline-none transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-navy-800/50 text-white px-4 py-3 rounded-xl border border-white/10 focus:border-gold-500 focus:outline-none cursor-pointer"
          >
            <option value="all">All Statuses</option>
            {Object.entries(LEAD_STATUSES).map(([key, config]) => (
              <option key={key} value={key}>{config.icon} {config.label}</option>
            ))}
          </select>

          {/* View Toggle */}
          <div className="flex gap-2 bg-navy-800/50 p-1 rounded-xl border border-white/10">
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                viewMode === 'kanban' ? 'bg-gold-500 text-navy-900' : 'text-white/60 hover:text-white'
              }`}
            >
              📋 Kanban
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                viewMode === 'table' ? 'bg-gold-500 text-navy-900' : 'text-white/60 hover:text-white'
              }`}
            >
              📊 Table
            </button>
          </div>
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {Object.entries(LEAD_STATUSES).map(([status, statusConfig]) => (
              <Droppable key={status} droppableId={status}>
                {(provided, snapshot) => (
                  <KanbanColumn
                    status={status}
                    statusConfig={statusConfig}
                    leads={leadsByStatus[status] || []}
                    provided={provided}
                    snapshot={snapshot}
                  />
                )}
              </Droppable>
            ))}
          </div>
        </DragDropContext>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-navy-900/50">
                  {['Name', 'Phone', 'Email', 'Budget', 'Purpose', 'Status', 'Temperature', 'Score'].map(header => (
                    <th key={header} className="text-gold-500 text-left px-6 py-4 text-sm font-bold border-b border-white/10 uppercase tracking-wide">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedLeads.map(lead => {
                  const tempConfig = TEMPERATURE_CONFIG[lead.temperature] || TEMPERATURE_CONFIG.cold;
                  const statusConfig = LEAD_STATUSES[lead.status] || LEAD_STATUSES.new;
                  
                  return (
                    <tr key={lead.id} className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer">
                      <td className="text-white px-6 py-4 text-sm font-medium">{lead?.name || lead?.full_name || 'Anonymous'}</td>
                      <td className="text-white px-6 py-4 text-sm">{lead?.phone || '-'}</td>
                      <td className="text-white px-6 py-4 text-sm">{lead?.email || '-'}</td>
                      <td className="text-gold-500 px-6 py-4 text-sm font-semibold">
                        {lead?.budget_min || lead?.budget_max
                          ? `${lead.budget_min ? `${(Number(lead.budget_min)/1000000).toFixed(1)}M` : ''} ${lead.budget_min && lead.budget_max ? '-' : ''} ${lead.budget_max ? `${(Number(lead.budget_max)/1000000).toFixed(1)}M` : ''}`
                          : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className="badge-gold text-xs">
                          {PURPOSE_LABELS[lead?.purpose] || lead?.purpose || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`${statusConfig.color} text-white text-xs px-3 py-1.5 rounded-full font-bold`}>
                          {statusConfig.icon} {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`${tempConfig.color} px-2 py-1 rounded-md text-xs font-bold border`}>
                          {tempConfig.emoji} {tempConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <TrendingUp size={16} className="text-green-400" />
                          <span className="text-white font-semibold">{lead?.lead_score || 0}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
              <p className="text-white/60 text-sm">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredLeads.length)} of {filteredLeads.length} leads
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg bg-navy-800/50 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-navy-700 transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="px-4 py-2 text-white">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg bg-navy-800/50 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-navy-700 transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Lead Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-navy-900 border border-white/10 p-6 rounded-2xl w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Add New Lead</h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-white/50 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const tenantId = localStorage.getItem('tenantId') || '1';
                await api.post(`/api/tenants/${tenantId}/leads`, newLeadForm);
                setIsAddModalOpen(false);
                setNewLeadForm({ name: '', phone: '', email: '', purpose: 'investment' });
                fetchLeads();
              } catch (err) {
                alert('Error creating lead: ' + err.message);
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-1">Name *</label>
                <input required type="text" className="w-full bg-navy-800 border border-white/10 rounded-lg p-2 text-white" value={newLeadForm.name} onChange={e => setNewLeadForm({...newLeadForm, name: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1">Phone</label>
                <input type="text" className="w-full bg-navy-800 border border-white/10 rounded-lg p-2 text-white" value={newLeadForm.phone} onChange={e => setNewLeadForm({...newLeadForm, phone: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1">Email</label>
                <input type="email" className="w-full bg-navy-800 border border-white/10 rounded-lg p-2 text-white" value={newLeadForm.email} onChange={e => setNewLeadForm({...newLeadForm, email: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1">Purpose</label>
                <select className="w-full bg-navy-800 border border-white/10 rounded-lg p-2 text-white" value={newLeadForm.purpose} onChange={e => setNewLeadForm({...newLeadForm, purpose: e.target.value})}>
                  <option value="investment">Investment</option>
                  <option value="living">Living</option>
                  <option value="residency">Residency</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 text-white/70 hover:text-white">Cancel</button>
                <button type="submit" className="btn-gold px-6 py-2">Save Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-navy-900 border border-white/10 p-6 rounded-2xl w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2"><Upload size={20} className="text-gold-400" /> Import Leads from CSV</h3>
              <button onClick={() => setIsImportModalOpen(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>

            {/* Drop Zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer mb-4 ${
                dragActive ? 'border-gold-400 bg-gold-500/10' : 'border-white/20 hover:border-white/40'
              }`}
              onClick={() => document.getElementById('csv-file-input').click()}
            >
              <Upload size={32} className="mx-auto mb-3 text-white/40" />
              <p className="text-white/70 text-sm">Drag & drop your CSV file here, or <span className="text-gold-400 font-medium">click to browse</span></p>
              <p className="text-white/40 text-xs mt-2">Required column: <strong>name</strong>. Optional: phone, email</p>
              <input id="csv-file-input" type="file" accept=".csv,.txt" className="hidden" onChange={e => handleFileUpload(e.target.files[0])} />
            </div>

            {/* Preview */}
            {importData.length > 0 && !importResult && (
              <div className="mb-4">
                <p className="text-sm text-green-400 mb-2">✅ {importData.length} leads found in file</p>
                <div className="max-h-48 overflow-y-auto rounded-lg border border-white/10">
                  <table className="w-full text-xs">
                    <thead className="bg-navy-800 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-white/60">#</th>
                        <th className="px-3 py-2 text-left text-white/60">Name</th>
                        <th className="px-3 py-2 text-left text-white/60">Phone</th>
                        <th className="px-3 py-2 text-left text-white/60">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importData.slice(0, 20).map((row, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="px-3 py-1.5 text-white/40">{i + 1}</td>
                          <td className="px-3 py-1.5 text-white">{row.name}</td>
                          <td className="px-3 py-1.5 text-white/70">{row.phone || '—'}</td>
                          <td className="px-3 py-1.5 text-white/70">{row.email || '—'}</td>
                        </tr>
                      ))}
                      {importData.length > 20 && (
                        <tr><td colSpan={4} className="px-3 py-2 text-center text-white/40 text-xs">... and {importData.length - 20} more</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Result */}
            {importResult && (
              <div className={`mb-4 p-4 rounded-lg border ${
                importResult.created > 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'
              }`}>
                <p className={`font-medium ${importResult.created > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {importResult.message}
                </p>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-red-300">
                    {importResult.errors.map((e, i) => <p key={i}>⚠️ {e}</p>)}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <button onClick={() => setIsImportModalOpen(false)} className="px-4 py-2 text-white/70 hover:text-white">Cancel</button>
              {!importResult && (
                <button
                  onClick={handleImportSubmit}
                  disabled={importData.length === 0 || importLoading}
                  className="btn-gold px-6 py-2 flex items-center gap-2 disabled:opacity-50"
                >
                  {importLoading ? <div className="w-4 h-4 border-2 border-navy-900 border-t-transparent rounded-full animate-spin" /> : <Upload size={16} />}
                  Import {importData.length} Leads
                </button>
              )}
              {importResult?.created > 0 && (
                <button onClick={() => { setIsImportModalOpen(false); setImportData([]); setImportResult(null); }} className="btn-gold px-6 py-2">Done</button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Schedule Modal */}
      {isScheduleModalOpen && selectedScheduleLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-navy-900 border border-white/10 p-6 rounded-2xl w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Clock className="text-gold-400" /> Schedule Follow-up
              </h3>
              <button onClick={() => setIsScheduleModalOpen(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            
            <form onSubmit={handleScheduleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Lead Name</label>
                <div className="w-full bg-navy-800 text-white rounded-lg px-4 py-2 opacity-70">
                  {selectedScheduleLead.name || selectedScheduleLead.full_name}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Message Tone / Strategy</label>
                <select 
                  value={scheduleForm.message_type}
                  onChange={(e) => setScheduleForm({...scheduleForm, message_type: e.target.value})}
                  className="w-full bg-navy-800 border border-white/10 text-white rounded-lg px-4 py-2 focus:outline-none focus:border-gold-500"
                >
                  <option value="fomo">Send Urgent Follow-up (FOMO)</option>
                  <option value="value">Send Offer Follow-up (Value Nudge)</option>
                  <option value="custom">Custom AI Generation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1 flex items-center justify-between">
                  <span>Custom Instruction for AI (Optional)</span>
                  <span className="text-[10px] bg-gold-500/20 text-gold-400 px-2 py-0.5 rounded-full border border-gold-500/30 font-bold uppercase tracking-wider animate-pulse">Smart</span>
                </label>
                <textarea 
                  value={scheduleForm.custom_prompt}
                  onChange={(e) => setScheduleForm({...scheduleForm, custom_prompt: e.target.value})}
                  placeholder="e.g., Ask if they want to see the 2BR in Downtown, or mention the new 1% payment plan..."
                  className="w-full bg-navy-800 border border-white/10 text-white rounded-lg px-4 py-2 focus:outline-none focus:border-gold-500 min-h-[80px] text-sm"
                />
                <p className="text-xs text-white/40 mt-1">If provided, the AI will use this exact instruction to generate a highly personalized, smart, and human-like follow-up message instead of using a generic template.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">Date & Time</label>
                <input 
                  type="datetime-local" 
                  required
                  value={scheduleForm.scheduled_at}
                  onChange={(e) => setScheduleForm({...scheduleForm, scheduled_at: e.target.value})}
                  className="w-full bg-navy-800 border border-white/10 text-white rounded-lg px-4 py-2 focus:outline-none focus:border-gold-500 [color-scheme:dark]"
                />
                <p className="text-xs text-white/40 mt-1">Select the exact time the bot will message the user.</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button type="button" onClick={() => setIsScheduleModalOpen(false)} className="px-4 py-2 text-white/70 hover:text-white">Cancel</button>
                <button 
                  type="submit" 
                  disabled={scheduleLoading || !scheduleForm.scheduled_at}
                  className="btn-gold px-6 py-2 disabled:opacity-50"
                >
                  {scheduleLoading ? 'Scheduling...' : 'Confirm Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedLeadsPage;
